import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import FetchingStatus from 'constants/statuses/FetchingStatus';

export type HotelMultimedia = {
  id: number;
  hotelId: number;
  fullUrl: string;
  sequence: number;
  toDelete: boolean;
};

export type HotelMultimediaExtendedProps = {
  key: number;
  orientation: 'landscape' | 'portrait' | 'square';
  size: string | null;
  dimensions: string;
  dpi: number | unknown;
} & HotelMultimedia;

export type HotelMultimediaExtended = {
  id: number;
  multimedia: HotelMultimediaExtendedProps[];
};

export type MultimediaState = {
  fetching: FetchingStatus | null;
  value: HotelMultimediaExtended[];
  updating: FetchingStatus | null;
  warningModal: { open: boolean };
  selectAll: boolean;
};

const initialState: MultimediaState = {
  fetching: null,
  value: [],
  updating: null,
  warningModal: { open: false },
  selectAll: false,
};

function setFetchingFunction(state: MultimediaState, action: PayloadAction<MultimediaState['fetching']>) {
  return {
    ...state,
    fetching: action.payload,
  };
}

function setUpdatingFunction(state: MultimediaState, action: PayloadAction<MultimediaState['updating']>) {
  return {
    ...state,
    updating: action.payload,
  };
}

function setHotelMultimediaFunction(state: MultimediaState, action: PayloadAction<MultimediaState['value']>) {
  const updated = action.payload;

  // If payload is empty, clear the state
  if (updated.length === 0) {
    return {
      ...state,
      value: [],
    };
  }

  const updatedIds = new Set(updated.map((item) => item.id));

  return {
    ...state,
    value: [
      // Keep existing items that are not being updated
      ...state.value.filter((item) => !updatedIds.has(item.id)),
      // Add the updated items
      ...updated,
    ],
  };
}

function setUpdateHotelMultimediaFunction(state: MultimediaState, action: PayloadAction<HotelMultimediaExtended>) {
  return {
    ...state,
    value: state.value.map((item) =>
      item.id === action.payload.id ? { ...item, multimedia: [...item.multimedia, ...action.payload.multimedia] } : item
    ),
  };
}

function setWarningModalFunction(state: MultimediaState, action: PayloadAction<MultimediaState['warningModal']>) {
  return {
    ...state,
    warningModal: action.payload,
  };
}

function setSelectAllMultimediaFunction(state: MultimediaState, action: PayloadAction<MultimediaState['selectAll']>) {
  return {
    ...state,
    selectAll: action.payload,
  };
}

const slice = createSlice({
  name: 'hotelManager.hotels.multimedia',
  initialState,
  reducers: {
    setFetching: setFetchingFunction,
    setHotelMultimedia: setHotelMultimediaFunction,
    setUpdating: setUpdatingFunction,
    setWarningModal: setWarningModalFunction,
    setUpdateHotelMultimedia: setUpdateHotelMultimediaFunction,
    setSelectAllMultimedia: setSelectAllMultimediaFunction,
  },
});

export const {
  setFetching,
  setHotelMultimedia,
  setUpdating,
  setWarningModal,
  setUpdateHotelMultimedia,
  setSelectAllMultimedia,
} = slice.actions;

export default slice.reducer;
